import { Alert, Divider, Form, Input } from 'antd';
import { filter, includes, keys, lowerCase, map, startCase } from 'lodash';
import React, { useContext, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../../AppContext';
import { InfoIcon } from '../../../../../../../../assets/svg';
import {
  ADDONS,
  APPROVAL_TYPE,
  BREAKPOINTS,
  PERMISSIONS,
} from '../../../../../../../../common/constants';
import { formValidatorRules } from '../../../../../../../../common/utils';
import CommonPopover from '../../../../../../../../components/CommonPopover';
import CommonSelect from '../../../../../../../../components/CommonSelect';
import CommonTreeSelect from '../../../../../../../../components/CommonTreeSelect';
import {
  EXTERNAL_APPROVER_TEAM_DROPDOWN_LIST,
  INTERNAL_APPROVER_TEAM_DROPDOWN_LIST,
} from '../../../../../../graphql/Queries';
import ApprovalTypeConfirmModal from './ApprovalTypeConfirmModal';

const { characterWithoutWhiteSpace } = formValidatorRules;

const ApproverLevels = ({
  form,
  projectId,
  showRfiApprovers,
  highlightedLevels = [],
  setHighlightedLevels,
  onApproverChange,
}) => {
  const { getTenantUser } = useContext(AppContext);
  const tenantUser = getTenantUser();
  const hasMultiLevelApproval = includes(
    tenantUser?.tenant?.addOn,
    ADDONS?.MULTI_LEVEL_APPROVAL,
  );
  const maxApprovalLevel = tenantUser?.tenant?.maxApprovalLevel || 1;
  const numberOfAllowedLevel = hasMultiLevelApproval
    ? Array.from({ length: maxApprovalLevel }, (_, i) => i + 1)
    : [1];
  const [approvalTypes, setApprovalTypes] = useState({});
  const [currentApprovalIndex, setCurrentApprovalIndex] = useState(null);
  const [showApprovalConfirmModal, setShowApprovalConfirmModal] =
    useState(false);
  const [selectedAllInternalIndex, setSelectedAllInternalIndex] = useState([]);
  const [selectedAllExternalIndex, setSelectedAllExternalIndex] = useState([]);
  const [selectedAllRfiInternal, setSelectedAllRfiInternal] = useState(false);
  const [selectedAllRfiExternal, setSelectedAllRfiExternal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  const handleExternalDeselect = (index) => {
    form?.setFieldsValue({
      approvers: {
        [index]: { externalUserApproverIds: [], externalTeamInfo: undefined },
      },
    });
  };

  const handleInternalDeselect = (index) => {
    form?.setFieldsValue({
      approvers: {
        [index]: { internalApproverIds: [], internalTeamInfo: undefined },
      },
    });
  };

  const handleTeamChange = (fieldPath, teamInfo) => {
    form?.setFieldsValue({
      [fieldPath?.[0]]:
        fieldPath?.length === 3
          ? { [fieldPath?.[1]]: { [fieldPath?.[2]]: teamInfo } }
          : { [fieldPath?.[1]]: teamInfo },
    });
  };

  return (
    <>
      <Alert
        showIcon
        title="Note: Only the current changes will be updated. All other assignments will remain unchanged."
        className="mb-10"
      />
      {showRfiApprovers && (
        <>
          <div className="fw-semi-bold font-size-16 mb-10">RFI Approvers</div>
          <div className="add-approval-form">
            <div className="d-flex align-center mb-10 justify-between">
              <div className="d-flex align-center gx-text-nowrap">
                <div className="title">RFI Approver</div>
              </div>
            </div>
            <Form.Item
              name={['rfiApprover', 'levelName']}
              label="Name"
              initialValue="RFI Approver"
              rules={[
                characterWithoutWhiteSpace,
                {
                  max: 30,
                  message: 'Name cannot be more than 30 characters',
                },
              ]}
            >
              <Input placeholder="RFI Approver Name" />
            </Form.Item>
            <Form.Item
              name={['rfiApprover', 'internalApproverIds']}
              dependencies={[
                ['rfiApprover', 'externalUserApproverIds'],
                ['rfiApprover', 'approvalType'],
              ]}
              label={
                <div className="d-flex justify-between width-percent-100">
                  <span>Internal Approvers</span>
                  {selectedAllRfiInternal && (
                    <span
                      className="text-primary pointer"
                      role="button"
                      tabIndex="0"
                      onClick={() => {
                        setSelectedAllRfiInternal(false);
                        form?.setFieldsValue({
                          rfiApprover: {
                            internalApproverIds: [],
                            internalTeamInfo: undefined,
                          },
                        });
                      }}
                    >
                      Deselect All
                    </span>
                  )}
                </div>
              }
              normalize={(value) => {
                onApproverChange?.();
                if (value?.includes?.('all')) {
                  setSelectedAllRfiInternal(true);
                  return ['all'];
                }
                return value;
              }}
            >
              <CommonTreeSelect
                treeCheckable
                showSearch
                allowClear
                hasSelectAll
                isSelectedAll={selectedAllRfiInternal}
                disabled={selectedAllRfiInternal}
                placeholder="Select Internal Approvers"
                query={INTERNAL_APPROVER_TEAM_DROPDOWN_LIST}
                variables={{ filter: { projectId } }}
                responsePath="internalApproverTeamDropdownList.data"
                valuePath="id"
                labelPath="user.name"
                optionKey="rfi-internal-approver"
                fetchPolicy="network-only"
                treeDefaultExpandAll
                virtual={false}
                parentValuePath="agencyId"
                parentLabelPath="agencyName"
                parentCountPath="userCount"
                childrenPath="users"
                open={activeDropdown === 'rfi-internal'}
                onOpenChange={(open) =>
                  setActiveDropdown(open ? 'rfi-internal' : null)
                }
                getPopupContainer={() =>
                  // eslint-disable-next-line no-undef
                  document.querySelector('.add-approver-modal')
                }
                onTeamChange={(teamInfo) =>
                  handleTeamChange(
                    ['rfiApprover', 'internalTeamInfo'],
                    teamInfo,
                  )
                }
              />
            </Form.Item>
            <Form.Item
              name={['rfiApprover', 'externalUserApproverIds']}
              dependencies={[
                ['rfiApprover', 'internalApproverIds'],
                ['rfiApprover', 'approvalType'],
              ]}
              label={
                <div className="d-flex justify-between width-percent-100">
                  <span>External Approvers</span>
                  {selectedAllRfiExternal && (
                    <span
                      className="text-primary pointer"
                      role="button"
                      tabIndex="0"
                      onClick={() => {
                        setSelectedAllRfiExternal(false);
                        form?.setFieldsValue({
                          rfiApprover: {
                            externalUserApproverIds: [],
                            externalTeamInfo: undefined,
                          },
                        });
                      }}
                    >
                      Deselect All
                    </span>
                  )}
                </div>
              }
              normalize={(value) => {
                onApproverChange?.();
                if (value?.includes?.('all')) {
                  setSelectedAllRfiExternal(true);
                  return ['all'];
                }
                return value;
              }}
            >
              <CommonTreeSelect
                treeCheckable
                showSearch
                allowClear
                hasSelectAll
                isSelectedAll={selectedAllRfiExternal}
                disabled={selectedAllRfiExternal}
                placeholder="Select External Approvers"
                query={EXTERNAL_APPROVER_TEAM_DROPDOWN_LIST}
                variables={{ filter: { projectId } }}
                responsePath="externalApproverTeamDropdownList.data"
                valuePath="id"
                labelPath="name"
                optionKey="rfi-external-approver"
                fetchPolicy="network-only"
                treeDefaultExpandAll
                virtual={false}
                parentValuePath="agencyId"
                parentLabelPath="agencyName"
                parentCountPath="userCount"
                childrenPath="users"
                open={activeDropdown === 'rfi-external'}
                onOpenChange={(open) =>
                  setActiveDropdown(open ? 'rfi-external' : null)
                }
                getPopupContainer={() =>
                  // eslint-disable-next-line no-undef
                  document.querySelector('.add-approver-modal')
                }
                onTeamChange={(teamInfo) =>
                  handleTeamChange(
                    ['rfiApprover', 'externalTeamInfo'],
                    teamInfo,
                  )
                }
              />
            </Form.Item>
          </div>
        </>
      )}
      <div className="fw-semi-bold font-size-16 mb-10">Stage Approvers</div>
      <div className="add-approval-form">
        {map(numberOfAllowedLevel, (index) => {
          const isLevelHighlighted = includes(highlightedLevels, index);
          const clearHighlight = () => {
            if (isLevelHighlighted) {
              setHighlightedLevels?.((prev) =>
                prev?.filter((level) => level !== index),
              );
            }
          };
          return (
            <div key={index}>
              <div className="d-flex align-center mb-10 justify-between">
                <div className="d-flex align-center gx-text-nowrap">
                  <div className="title">{`Level ${index}`}</div>
                </div>
                <div className="d-flex align-center">
                  <Form.Item
                    name={['approvers', index, 'approvalType']}
                    className="mb-0"
                  >
                    <CommonSelect
                      placeholder="Approval Type"
                      allowClear
                      className="approvalType-select"
                      onChange={(e) => {
                        setApprovalTypes((prev) => ({ ...prev, [index]: e }));
                        if (
                          e === APPROVAL_TYPE.ON_SITE &&
                          form?.getFieldValue([
                            'approvers',
                            index,
                            'externalUserApproverIds',
                          ])?.length > 0
                        ) {
                          form?.setFieldsValue({
                            approvers: {
                              [index]: {
                                approvalType: APPROVAL_TYPE.REMOTE,
                              },
                            },
                          });
                          setApprovalTypes((prev) => ({
                            ...prev,
                            [index]: APPROVAL_TYPE.REMOTE,
                          }));
                          setCurrentApprovalIndex(index);
                          setShowApprovalConfirmModal(true);
                        } else if (e === APPROVAL_TYPE.ON_SITE) {
                          handleExternalDeselect(index);
                        }
                      }}
                      options={[
                        ...map(keys(APPROVAL_TYPE), (status) => {
                          return {
                            key: status,
                            value: status,
                            label: startCase(lowerCase(status)),
                          };
                        }),
                      ]}
                    />
                  </Form.Item>
                  <div>
                    <CommonPopover
                      placement="left"
                      classNames={{ root: 'nomenclature-info' }}
                      content={
                        <>
                          <div>
                            <b>On-Site approval</b> requires the approver to be
                            physically present on site and within the project
                            radius and complete the process via the mobile app
                            only. External approvers cannot be assigned when
                            on-site approval is selected.
                          </div>
                          <div className="mt-10">
                            <b>Remote approval</b> can be done from any
                            location, via both web and mobile, with flexible
                            photo options (camera or gallery).
                          </div>
                        </>
                      }
                      trigger={[!isDesktopViewport ? 'click' : 'hover']}
                      getPopupContainer={() =>
                        // eslint-disable-next-line no-undef
                        document.querySelector('.add-approver-modal')
                      }
                    >
                      <InfoIcon
                        className="ml-5 mt-5 nomenclature-info-button"
                        height="18px"
                        width="18px"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </CommonPopover>
                  </div>
                </div>
              </div>
              <Form.Item
                name={['approvers', index, 'levelName']}
                rules={[
                  characterWithoutWhiteSpace,
                  {
                    max: 30,
                    message: 'Name cannot be more than 30 characters',
                  },
                ]}
                label="Name"
                initialValue={`Level ${index}`}
              >
                <Input placeholder="Level Name" />
              </Form.Item>
              <Form.Item
                className="mt-10"
                name={['approvers', index, 'internalApproverIds']}
                dependencies={[
                  ['approvers', index, 'externalUserApproverIds'],
                  ['approvers', index, 'approvalType'],
                ]}
                validateStatus={isLevelHighlighted ? 'error' : undefined}
                help={isLevelHighlighted ? 'Assign approvers here' : undefined}
                label={
                  <div className="d-flex justify-between width-percent-100">
                    <span>Internal Approvers</span>
                    {includes(selectedAllInternalIndex, index) && (
                      <span
                        className="text-primary pointer"
                        role="button"
                        tabIndex="0"
                        onClick={() => {
                          setSelectedAllInternalIndex(
                            filter(
                              selectedAllInternalIndex,
                              (i) => i !== index,
                            ),
                          );
                          handleInternalDeselect(index);
                        }}
                      >
                        Deselect All
                      </span>
                    )}
                  </div>
                }
                normalize={(value) => {
                  clearHighlight();
                  onApproverChange?.();
                  if (value?.includes?.('all')) {
                    setSelectedAllInternalIndex((prev) => [...prev, index]);
                    return ['all'];
                  }
                  return value;
                }}
              >
                <CommonTreeSelect
                  treeCheckable
                  showSearch
                  allowClear
                  hasSelectAll
                  isSelectedAll={includes(selectedAllInternalIndex, index)}
                  placeholder="Select Internal Approvers"
                  query={INTERNAL_APPROVER_TEAM_DROPDOWN_LIST}
                  variables={{
                    filter: {
                      projectId,
                    },
                  }}
                  responsePath="internalApproverTeamDropdownList.data"
                  valuePath="id"
                  labelPath="user.name"
                  optionKey={`internal-approver-${index}`}
                  fetchPolicy="network-only"
                  disabled={includes(selectedAllInternalIndex, index)}
                  treeDefaultExpandAll
                  virtual={false}
                  parentValuePath="agencyId"
                  parentLabelPath="agencyName"
                  parentCountPath="userCount"
                  childrenPath="users"
                  open={activeDropdown === `internal-${index}`}
                  onOpenChange={(open) =>
                    setActiveDropdown(open ? `internal-${index}` : null)
                  }
                  getPopupContainer={() =>
                    // eslint-disable-next-line no-undef
                    document.querySelector('.add-approver-modal')
                  }
                  onTeamChange={(teamInfo) =>
                    handleTeamChange(
                      ['approvers', index, 'internalTeamInfo'],
                      teamInfo,
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                name={['approvers', index, 'externalUserApproverIds']}
                validateStatus={
                  isLevelHighlighted &&
                  approvalTypes?.[index] !== APPROVAL_TYPE.ON_SITE
                    ? 'error'
                    : undefined
                }
                help={
                  isLevelHighlighted &&
                  approvalTypes?.[index] !== APPROVAL_TYPE.ON_SITE
                    ? 'Assign approvers here'
                    : undefined
                }
                dependencies={[
                  ['approvers', index, 'internalApproverIds'],
                  ['approvers', index, 'approvalType'],
                ]}
                rules={[]}
                label={
                  <div className="d-flex justify-between width-percent-100">
                    <span>External Approvers</span>
                    {includes(selectedAllExternalIndex, index) && (
                      <span
                        className="text-primary pointer"
                        role="button"
                        tabIndex="0"
                        onClick={() => {
                          setSelectedAllExternalIndex(
                            filter(
                              selectedAllExternalIndex,
                              (i) => i !== index,
                            ),
                          );
                          handleExternalDeselect(index);
                        }}
                      >
                        Deselect All
                      </span>
                    )}
                  </div>
                }
                normalize={(value) => {
                  clearHighlight();
                  onApproverChange?.();
                  if (value?.includes?.('all')) {
                    setSelectedAllExternalIndex((prev) => [...prev, index]);
                    return ['all'];
                  }
                  return value;
                }}
              >
                <CommonTreeSelect
                  treeCheckable
                  showSearch
                  allowClear
                  hasSelectAll
                  isSelectedAll={includes(selectedAllExternalIndex, index)}
                  placeholder="Select External Approvers"
                  query={EXTERNAL_APPROVER_TEAM_DROPDOWN_LIST}
                  variables={{
                    filter: {
                      projectId,
                    },
                  }}
                  responsePath="externalApproverTeamDropdownList.data"
                  valuePath="id"
                  labelPath="name"
                  optionKey={`external-approver-${index}`}
                  fetchPolicy="network-only"
                  disabled={
                    includes(selectedAllExternalIndex, index) ||
                    approvalTypes?.[index] === APPROVAL_TYPE.ON_SITE
                  }
                  treeDefaultExpandAll
                  virtual={false}
                  parentValuePath="agencyId"
                  parentLabelPath="agencyName"
                  parentCountPath="userCount"
                  childrenPath="users"
                  open={activeDropdown === `external-${index}`}
                  onOpenChange={(open) =>
                    setActiveDropdown(open ? `external-${index}` : null)
                  }
                  getPopupContainer={() =>
                    // eslint-disable-next-line no-undef
                    document.querySelector('.add-approver-modal')
                  }
                  onTeamChange={(teamInfo) =>
                    handleTeamChange(
                      ['approvers', index, 'externalTeamInfo'],
                      teamInfo,
                    )
                  }
                />
              </Form.Item>
              {approvalTypes?.[index] === APPROVAL_TYPE.ON_SITE && (
                <div className="text-danger error-text">
                  External approvers cannot be assigned when on-site approval is
                  selected
                </div>
              )}
              {numberOfAllowedLevel?.length > index && <Divider dashed />}
            </div>
          );
        })}
      </div>
      {showApprovalConfirmModal && (
        <ApprovalTypeConfirmModal
          showModal={showApprovalConfirmModal}
          hasExternalApproval={
            form?.getFieldValue([
              'approvers',
              currentApprovalIndex,
              'externalUserApproverIds',
            ])?.length > 0
          }
          eqcTypeData={{ project: { permissions: [PERMISSIONS.LOCATION] } }}
          handleOk={() => {
            handleExternalDeselect(currentApprovalIndex);
            form?.setFieldsValue({
              approvers: {
                [currentApprovalIndex]: {
                  approvalType: APPROVAL_TYPE.ON_SITE,
                },
              },
            });
            setApprovalTypes((prev) => ({
              ...prev,
              [currentApprovalIndex]: APPROVAL_TYPE.ON_SITE,
            }));
            setShowApprovalConfirmModal(false);
          }}
          handleCancel={() => {
            setShowApprovalConfirmModal(false);
          }}
        />
      )}
    </>
  );
};

export default ApproverLevels;
